"use client";

import { adminApi, clearClientToken, setClientToken } from "@onsand/api";
import { auth } from "@onsand/auth";
import {
	OAuthProvider,
	signInWithEmailAndPassword,
	signInWithPopup,
	type UserCredential,
} from "firebase/auth";
import { useRouter } from "next/navigation";
import { type FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Eye, EyeSlash, Lock, Sms, Warning2 } from "@/lib/iconsax";
import { cn } from "@/lib/utils";
import { admitStaff, type StaffSession } from "./admit-staff";
import { signInErrorMessage } from "./sign-in-error";

const ACCESS_DENIED = "Access denied. Admin privileges required.";

const STAFF_SESSION: StaffSession = {
	clearToken: clearClientToken,
	endSession: () => auth.signOut(),
	fetchRole: async () => (await adminApi.session.me()).role,
	storeToken: setClientToken,
};

const APPLE_PROVIDER = new OAuthProvider("apple.com");
APPLE_PROVIDER.addScope("email");
APPLE_PROVIDER.addScope("name");

// Apple's mark; the icon set has no brand glyphs.
function AppleGlyph({ className }: { className?: string }) {
	return (
		<svg aria-hidden="true" className={className} fill="currentColor" viewBox="0 0 24 24">
			<path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
		</svg>
	);
}

const FIELD =
	"h-12 rounded-xl text-base shadow-none sm:text-base [&_input]:h-12 [&_input]:text-base sm:[&_input]:h-12 sm:[&_input]:text-base";
const FIELD_ICON =
	"pointer-events-none absolute top-1/2 left-3.5 z-10 size-5 -translate-y-1/2 text-muted-foreground transition-colors group-has-[input:focus-visible]/field:text-ring";
const LABEL = "text-base/5 sm:text-base/5";

export function LoginForm({ className }: { className?: string }) {
	const router = useRouter();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	async function signInWith(authenticate: () => Promise<UserCredential>) {
		setLoading(true);
		setError(null);

		try {
			const { user } = await authenticate();
			if ((await admitStaff(user, STAFF_SESSION)) === "denied") {
				setError(ACCESS_DENIED);
				return;
			}

			router.push("/");
		} catch (err) {
			setError(signInErrorMessage(err));
		} finally {
			setLoading(false);
		}
	}

	function handleSubmit(e: FormEvent) {
		e.preventDefault();
		return signInWith(() => signInWithEmailAndPassword(auth, email, password));
	}

	const handleApple = () => signInWith(() => signInWithPopup(auth, APPLE_PROVIDER));

	return (
		<section
			aria-labelledby="login-title"
			className={cn(
				"flex flex-col justify-center bg-card px-6 py-10 sm:px-10 lg:px-16 lg:py-16",
				className,
			)}
		>
			<div className="mx-auto w-full max-w-100">
				<h1
					className="text-balance font-[650] text-[2rem] leading-[1.08] tracking-[-0.02em]"
					id="login-title"
				>
					Welcome back
				</h1>
				<p className="mt-2 text-base text-muted-foreground">Sign in to the admin panel</p>

				<form className="mt-8 grid gap-5" onSubmit={handleSubmit}>
					<div className="grid gap-2">
						<Label className={LABEL} htmlFor="email">
							Email
						</Label>
						<div className="group/field relative">
							<Sms aria-hidden="true" className={FIELD_ICON} variant="Linear" />
							<Input
								autoCapitalize="none"
								autoComplete="username"
								className={cn(FIELD, "[&_input]:ps-11")}
								id="email"
								inputMode="email"
								name="email"
								onChange={(e) => setEmail(e.target.value)}
								placeholder="name@example.com"
								required
								spellCheck={false}
								type="email"
								value={email}
							/>
						</div>
					</div>

					<div className="grid gap-2">
						<Label className={LABEL} htmlFor="password">
							Password
						</Label>
						<div className="group/field relative">
							<Lock aria-hidden="true" className={FIELD_ICON} variant="Linear" />
							<Input
								autoComplete="current-password"
								className={cn(FIELD, "[&_input]:ps-11 [&_input]:pe-12")}
								id="password"
								name="password"
								onChange={(e) => setPassword(e.target.value)}
								required
								type={showPassword ? "text" : "password"}
								value={password}
							/>
							<Button
								aria-label={showPassword ? "Hide password" : "Show password"}
								className="absolute top-1/2 right-1 z-10 -translate-y-1/2 text-muted-foreground hover:text-foreground"
								onClick={() => setShowPassword((visible) => !visible)}
								size="icon"
								type="button"
								variant="ghost"
							>
								{showPassword ? (
									<EyeSlash className="size-5" variant="Linear" />
								) : (
									<Eye className="size-5" variant="Linear" />
								)}
							</Button>
						</div>
					</div>

					{error && (
						<p
							className="flex items-start gap-2 text-pretty text-destructive-foreground text-sm"
							role="alert"
						>
							<Warning2 aria-hidden="true" className="mt-0.5 size-4 shrink-0" variant="Linear" />
							{error}
						</p>
					)}

					<Button
						className="mt-1 h-12 w-full rounded-full font-semibold text-base sm:h-12 sm:text-base"
						disabled={loading}
						type="submit"
					>
						{loading ? (
							<>
								<Spinner className="size-5" />
								Signing in…
							</>
						) : (
							"Sign in"
						)}
					</Button>
				</form>

				<div
					aria-hidden="true"
					className="my-6 flex items-center gap-4 text-muted-foreground text-sm"
				>
					<span className="h-px flex-1 bg-border" />
					or
					<span className="h-px flex-1 bg-border" />
				</div>

				<Button
					className="h-12 w-full rounded-full font-semibold text-base sm:h-12 sm:text-base"
					disabled={loading}
					onClick={handleApple}
					type="button"
					variant="outline"
				>
					<AppleGlyph className="size-5" />
					Continue with Apple
				</Button>
			</div>
		</section>
	);
}
